import { Badge, createDataTableColumnHelper, DataTable, Switch } from "@beweegbaas/ui";
import { createFileRoute, getRouteApi, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState } from "react";
import { toast } from "@beweegbaas/ui";
import { DataTableActions, type DataTableAction } from "#/components/data-table-actions";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { CreateInviteButton } from "#/modules/admin/components/create-invite-button";
import { SchoolDetailHeader } from "#/modules/admin/components/school-detail-header";
import {
  deleteInviteFn,
  removeDocentFn,
  resendInviteFn,
  setHoofdDocentFn,
} from "#/modules/admin/schools.functions";
import { InviteMailStatusBadge } from "#/modules/school.invite/components/invite-mail-status-badge";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";

const layoutApi = getRouteApi("/$locale/{-$schoolyear}/_authenticated/admin/schools/$id");

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/admin/schools/$id/docenten",
)({
  component: AdminSchoolDocenten,
  errorComponent: DocentenError,
});

function DocentenError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

type DocentModel = components["schemas"]["DocentOverviewAdminModel"];
type InviteModel = components["schemas"]["SchooldocentInviteOverviewModel"];

/** Docenten and pending invites share the table; rows discriminate on `kind`. */
type DocentenRow =
  | { kind: "docent"; docent: DocentModel }
  | { kind: "invite"; invite: InviteModel };

const formatDateTime = (dateString: string) =>
  new Date(dateString).toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function DocentActions({ docent }: { docent: DocentModel }) {
  const { id: schoolId } = Route.useParams();
  const router = useRouter();
  const setHoofdDocent = useServerFn(setHoofdDocentFn);
  const removeDocent = useServerFn(removeDocentFn);
  const [isHoofdDocent, setIsHoofdDocent] = useState(docent.isHoofdDocent);

  async function handleSetHoofdDocent(isSelected: boolean) {
    setIsHoofdDocent(isSelected);
    try {
      await setHoofdDocent({ data: { schoolId, schoolDocentId: docent.docentId } });
    } catch {
      // toastErrorMiddleware already surfaced the error; revert the optimistic switch
      setIsHoofdDocent(!isSelected);
      return;
    }
    await router.invalidate();
  }

  return (
    <DataTableActions
      actions={[
        {
          key: "remove",
          label: m["common.actions.unlink"](),
          icon: <IconMaterialSymbolsDelete className="size-4" />,
          destructive: true,
          disabled: !docent.canBeRemoved,
          confirm: {
            title: m["common.confirm.unlink_docent.title"](),
            message: m["common.confirm.unlink_docent.message"]({
              docentName: docent.name,
            }),
          },
          onClick: async () => {
            await removeDocent({ data: { schoolId, schooldocentId: docent.docentId } });
            toast.success(m["admin.scholen.docenten.docent_verwijderd_toast"]());
            await router.invalidate();
          },
        },
      ]}
    >
      <div className="flex items-center gap-2">
        <Switch
          checked={isHoofdDocent}
          onCheckedChange={(checked) => void handleSetHoofdDocent(checked)}
          disabled={!docent.canBeMadeHoofdDocent}
        />
        <label className="text-sm">{m["common.labels.hoofddocent"]()}</label>
      </div>
    </DataTableActions>
  );
}

function InviteActions({ invite }: { invite: InviteModel }) {
  const router = useRouter();
  const resendInvite = useServerFn(resendInviteFn);
  const deleteInvite = useServerFn(deleteInviteFn);

  const handleResend = async () => {
    try {
      await resendInvite({ data: { inviteId: invite.id } });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    toast.success(m["school.docenten.success.invite_resent"]());
    await router.invalidate();
  };

  if (!invite.canResend && !invite.canDelete) return null;

  const actions: DataTableAction[] = [
    ...(invite.canResend
      ? [
          {
            key: "resend",
            label: m["school.docenten.actions.resend"](),
            icon: <IconMaterialSymbolsRefresh />,
            onClick: () => void handleResend(),
          },
        ]
      : []),
    ...(invite.canDelete
      ? [
          {
            key: "delete",
            label: m["school.docenten.invite.delete.confirm_title"](),
            icon: <IconMaterialSymbolsDelete />,
            destructive: true,
            confirm: {
              title: m["school.docenten.invite.delete.confirm_title"](),
              message: m["school.docenten.invite.delete.confirm_message"]({
                email: invite.inviteeEmail,
              }),
              confirmLabel: m["school.docenten.invite.delete.confirm_title"](),
            },
            onClick: async () => {
              await deleteInvite({ data: { inviteId: invite.id } });
              toast.success(m["school.docenten.uitnodiging_verwijderd_toast"]());
              await router.invalidate();
            },
          },
        ]
      : []),
  ];

  return <DataTableActions actions={actions} />;
}

const helper = createDataTableColumnHelper<DocentenRow>();

const columns = helper.columns([
  helper.display({
    id: "name",
    header: () => m["common.fields.name"](),
    cell: ({ row }) =>
      row.original.kind === "docent" ? (
        <div className="flex items-center gap-2">
          {row.original.docent.name}
          {row.original.docent.isHoofdDocent && (
            <Badge variant="blue" size="sm">
              {m["common.labels.hoofddocent"]()}
            </Badge>
          )}
        </div>
      ) : (
        "-"
      ),
  }),
  helper.display({
    id: "email",
    header: () => m["common.fields.email"](),
    cell: ({ row }) =>
      row.original.kind === "docent" ? row.original.docent.email : row.original.invite.inviteeEmail,
  }),
  helper.display({
    id: "status",
    header: () => m["common.fields.status"](),
    cell: ({ row }) =>
      row.original.kind === "docent" ? (
        <Badge variant="green" size="sm">
          {m["common.status.actief"]()}
        </Badge>
      ) : (
        <div className="flex flex-wrap items-center gap-1">
          <Badge variant="gray" size="sm">
            {m["school.docenten.status.pending"]()}
          </Badge>
          <InviteMailStatusBadge delivery={row.original.invite.mailDelivery} />
        </div>
      ),
  }),
  helper.display({
    id: "lastActivity",
    header: () => m["school.docenten.table.last_activity"](),
    cell: ({ row }) => {
      if (row.original.kind === "invite") {
        return m["school.docenten.invite.expires_at"]({
          date: formatDateTime(row.original.invite.expiresAt),
        });
      }
      const lastActivityAt = row.original.docent.lastActivityAt;
      return lastActivityAt ? formatDateTime(lastActivityAt) : m["common.labels.never_logged_in"]();
    },
  }),
  helper.display({
    id: "actions",
    meta: { action: true, headerClassName: "w-0" },
    cell: ({ row }) =>
      row.original.kind === "docent" ? (
        <DocentActions docent={row.original.docent} />
      ) : (
        <InviteActions invite={row.original.invite} />
      ),
  }),
]);

function AdminSchoolDocenten() {
  const { id } = Route.useParams();
  const { docenten, docentenCount, school, invites, canAddDocent } = layoutApi.useLoaderData();

  if (!school) {
    return null;
  }

  const pendingInvites = invites.filter((invite) => invite.status === "Pending");
  const rows: DocentenRow[] = [
    ...docenten.map((docent) => ({ kind: "docent" as const, docent })),
    ...pendingInvites.map((invite) => ({ kind: "invite" as const, invite })),
  ];

  const inviteButton = canAddDocent ? (
    <CreateInviteButton className="max-sm:w-full" size="xl" schoolId={id}>
      <IconMaterialSymbolsAdd />
      {m["school.docenten.actions.invite"]()}
    </CreateInviteButton>
  ) : null;

  return (
    <Page>
      <SchoolDetailHeader
        schoolName={school.name}
        schoolId={id}
        docentenCount={docentenCount}
        maxDocenten={school.maxDocenten}
        actions={inviteButton}
      />
      <DataTable
        alignToContainer
        aria-label={m["common.labels.docenten"]()}
        columns={columns}
        data={rows}
        getRowId={(row) => (row.kind === "docent" ? row.docent.docentId : row.invite.id)}
        empty={m["common.empty_state.no_docenten"]()}
      />
    </Page>
  );
}
